import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiMapMarker, mdiSchool } from "@mdi/js";
import { useAuth } from "../../contexts/AuthContext";
import { organizationApi } from "../../api/organization";
import AppHeader from "../../components/AppHeader";
import foxMapPin from "../../assets/logo_fox_map_pin_v4-600h.png";
import "../Common.css";
import "../Dashboard.css";
import "../header.css";
import "./ClassesPortal.css";

/**
 * Renders children only when the org has a business address set.
 * Otherwise renders a single "Welcome to Class Management" setup card —
 * same friendly prompt on the portal AND on any deep-linked /classes/*
 * page, so admins never land on an empty, silently-broken screen.
 *
 * Pessimistic render: while the org-profile fetch is in flight, renders
 * a minimal "Loading..." state instead of children. This avoids the
 * deep-link race where ClassStudentsPage / ClassTermsPage mount, fire
 * API calls that 412, and pop a toast before the gate flips. For the
 * common case (orgLocation IS set) the loading state is a sub-second
 * flicker before the real page renders.
 */
const ClassesSetupGate = ({ children }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [hasOrgLocation, setHasOrgLocation] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const loadOrg = async () => {
            if (!user?.organisationId) {
                if (!cancelled) setHasOrgLocation(true); // no org context — fail-open, let route guards handle
                return;
            }
            try {
                const org = await organizationApi.getOrgProfile(user.organisationId);
                if (cancelled) return;
                const present = Boolean(org?.orgLocation?.latitude && org?.orgLocation?.longitude);
                setHasOrgLocation(present);
            } catch (_) {
                if (!cancelled) setHasOrgLocation(true); // fetch error — fail-open
            }
        };
        loadOrg();
        return () => { cancelled = true; };
    }, [user?.organisationId]);

    // Pre-resolution: hide everything except chrome to avoid 412-toast flash on deep-links.
    if (hasOrgLocation === null) {
        return (
            <div className="dashboard-container org-console">
                <AppHeader />
                <div className="dashboard-main">
                    <div className="dashboard-content-wrapper" style={{ maxWidth: "900px" }}>
                        <p style={{ textAlign: "center", padding: "48px 0", color: "var(--dl-color-text-muted)" }}>
                            Loading...
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (!hasOrgLocation) {
        return (
            <div className="dashboard-container org-console">
                <AppHeader />
                <div className="dashboard-main">
                    <div className="dashboard-content-wrapper" style={{ maxWidth: "900px" }}>
                        <div className="sub-portal-header-card">
                            <div className="sub-portal-header-title-row">
                                <button onClick={() => navigate("/dashboard")} className="back-nav-btn">
                                    ← Dashboard
                                </button>
                                <h1 className="sub-portal-header-title">
                                    <Icon
                                        path={mdiSchool}
                                        size={1.2}
                                        color="var(--dl-color-purple-default)"
                                        style={{ marginRight: "8px", verticalAlign: "middle" }}
                                    />
                                    Classes
                                </h1>
                            </div>
                        </div>
                        <div className="content-card classes-portal-setup-card">
                            <img
                                src={foxMapPin}
                                alt=""
                                className="classes-portal-setup-icon"
                            />
                            <h2 className="classes-portal-setup-title">Welcome to Class Management</h2>
                            <p className="classes-portal-setup-body">
                                To get started, please add your studio&rsquo;s address in Settings.
                            </p>
                            <button
                                className="btn-purple classes-portal-setup-cta"
                                onClick={() => navigate("/settings")}>
                                <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <Icon path={mdiMapMarker} size={0.8} />
                                    Set Studio Address
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return children;
};

export default ClassesSetupGate;
